import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import {
  Phone,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  ShoppingBag,
  Star,
  MapPin,
  AlertTriangle,
} from "lucide-react";
import Header from "./site/Header";
import Footer from "./site/Footer";
import Contact from "./site/Contact";
import QuoteButton from "./site/QuoteButton";
import PromoCard from "./site/PromoCard";
import VehicleTierLookup from "./site/VehicleTierLookup";
import { TOWING_PROMO } from "../data/promo";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { SITE } from "../site.config";
import { SERVICES, findService } from "../data/services";
import { REVIEWS } from "../data/reviews";
import { BUILDS } from "../data/builds";
import { POSTS } from "../data/blog";
import { INDEXABLE_SUBURBS, SUBURB_PATH } from "../suburbs";
import useDocumentMeta from "../hooks/useDocumentMeta";

const SITE_URL = "https://powerzautoelectrical.com.au";

export default function ServicePage() {
  const { slug } = useParams();
  const service = findService(slug);

  // Hook must be called unconditionally — use safe defaults when service is missing
  const safe = service || {};
  const canonical = `${SITE_URL}/services/${slug || ""}`;

  const serviceJsonLd = service && {
    "@context": "https://schema.org",
    "@type": "Service",
    name: safe.name,
    serviceType: safe.name,
    description: safe.metaDesc,
    url: canonical,
    image: safe.image ? `${SITE_URL}${safe.image}` : undefined,
    provider: {
      "@type": "AutoRepair",
      name: SITE.business.fullName,
      telephone: SITE.business.phoneTel,
      url: SITE_URL,
      address: {
        "@type": "PostalAddress",
        streetAddress: SITE.business.address.line1,
        addressLocality: SITE.business.address.suburb,
        addressRegion: SITE.business.address.state,
        postalCode: SITE.business.address.postcode,
        addressCountry: SITE.business.address.country,
      },
    },
    areaServed: {
      "@type": "AdministrativeArea",
      name: "Sunshine Coast, QLD",
    },
    offers: safe.priceFrom && safe.priceFrom.startsWith("$")
      ? {
          "@type": "Offer",
          price: safe.priceFrom.replace(/[^0-9.]/g, ""),
          priceCurrency: "AUD",
          availability: "https://schema.org/InStock",
          url: canonical,
        }
      : undefined,
  };

  const faqJsonLd = service && {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: (safe.faq || []).map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const breadcrumbJsonLd = service && {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      {
        "@type": "ListItem",
        position: 2,
        name: "Services",
        item: `${SITE_URL}/#services`,
      },
      { "@type": "ListItem", position: 3, name: safe.name, item: canonical },
    ],
  };

  useDocumentMeta({
    title: safe.metaTitle || "Service | Powerz Auto Electrical",
    description: safe.metaDesc || "",
    canonical,
    image: safe.image
      ? `${SITE_URL}${safe.image}`
      : SITE.business.logo,
    jsonLd: service
      ? [serviceJsonLd, faqJsonLd, breadcrumbJsonLd].filter(Boolean)
      : [],
  });

  if (!service) {
    return <Navigate to="/" replace />;
  }

  // Pull 3 reviews tagged with related service categories
  const relatedReviews = (() => {
    const tags = service.relatedReviewService || [];
    if (!tags.length) return [];
    const matches = REVIEWS.filter((r) =>
      tags.some((t) => r.service && r.service.toLowerCase() === t.toLowerCase())
    );
    // Featured first, then the rest
    const sorted = [
      ...matches.filter((r) => r.featured),
      ...matches.filter((r) => !r.featured),
    ];
    return sorted.slice(0, 3);
  })();

  // Cluster-first related services — explicit relatedServices takes precedence,
  // fallback to fill-in with other services when the cluster is small.
  const clusterSlugs = Array.isArray(service.relatedServices) ? service.relatedServices : [];
  const clusterServices = clusterSlugs
    .map((sl) => SERVICES.find((s) => s.slug === sl))
    .filter(Boolean)
    .filter((s) => s.slug !== service.slug);
  const filler = SERVICES.filter(
    (s) => s.slug !== service.slug && !clusterSlugs.includes(s.slug)
  );
  const otherServices = [...clusterServices, ...filler].slice(0, 6);
  const shopCategory = service.relatedShop
    ? SITE.shop.categories.find((c) => c.slug === service.relatedShop)
    : null;

  // Find builds that match this service (internal linking for topical authority)
  const relatedBuilds = BUILDS.filter(
    (b) => Array.isArray(b.relatedServices) && b.relatedServices.includes(service.slug)
  ).slice(0, 3);

  // Cross-link related blog posts (declared per-service in services.js)
  const relatedPosts = Array.isArray(service.relatedBlogs)
    ? service.relatedBlogs
        .map((slug) => POSTS.find((p) => p.slug === slug))
        .filter(Boolean)
    : [];

  // 8 suburbs we feature most prominently — internal-link block for local SEO
  // Only indexable suburbs are linked; other featured mentions live in the copy.
  const featuredSuburbs = INDEXABLE_SUBURBS.slice(0, 8);

  return (
    <div className="bg-black text-white">
      <Header />

      {/* HERO */}
      <section
        data-testid={`service-hero-${service.slug}`}
        className="relative min-h-[70svh] w-full overflow-hidden bg-black pt-40 md:pt-48"
      >
        <div
          className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{ backgroundImage: `url(${service.image})` }}
          aria-hidden="true"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.7) 60%, #000 100%)",
          }}
          aria-hidden="true"
        />
        <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 pb-20">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/60 hover:text-cyan-pz transition-colors bebas tracking-widest text-sm mb-10"
            data-testid="service-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            ALL SERVICES
          </Link>

          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-cyan-pz" />
            <span className="bebas text-cyan-pz tracking-[0.3em] text-sm md:text-base">
              SUNSHINE COAST • {service.kicker.toUpperCase()}
            </span>
          </div>

          <h1
            data-testid="service-headline"
            className="bebas text-white text-5xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tight max-w-5xl"
          >
            <span className="text-cyan-pz glow-cyan">{service.h1.toUpperCase()}</span>
          </h1>

          {service.priceFrom && (
            <div className="mt-8 inline-flex items-center gap-3 border border-cyan-pz/40 bg-black/60 backdrop-blur-sm px-5 py-3">
              <span className="bebas text-white/60 tracking-widest text-xs">
                FROM
              </span>
              <span className="bebas text-cyan-pz text-3xl tracking-wider">
                {service.priceFrom}
              </span>
            </div>
          )}

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <a
              href={`tel:${SITE.business.phoneTel}`}
              data-testid="service-call-btn"
              className="inline-flex items-center justify-center gap-3 bg-cyan-pz text-black px-8 py-5 bebas text-xl tracking-[0.2em] hover:bg-white transition-colors clip-perf pulse-cyan"
            >
              <Phone className="w-5 h-5" />
              CALL {SITE.business.phone}
            </a>
            <QuoteButton variant="outline" testId="service-quote-btn" context={service.name} />
          </div>
          {TOWING_PROMO.serviceSlugs.includes(service.slug) && <PromoCard compact />}
          {service.slug === "redarc-brake-controllers" && (
            <div className="mt-8 max-w-3xl">
              <VehicleTierLookup />
            </div>
          )}

          <div className="mt-10 flex items-center gap-2 text-white/60 text-sm">
            <MapPin className="w-4 h-4 text-cyan-pz" />
            Workshop: {SITE.business.address.full} • Servicing all Sunshine Coast
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="relative bg-black py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 space-y-6">
          {service.intro.map((p, i) => (
            <p
              key={i}
              className="text-white/80 barlow text-lg md:text-xl leading-relaxed"
            >
              {p}
            </p>
          ))}
        </div>
      </section>

      {/* WHAT'S INCLUDED + WHY US */}
      <section className="relative bg-[#0A0A0A] py-20 md:py-28 border-y border-white/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div data-testid="service-whats-included">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-cyan-pz" />
              <span className="bebas text-cyan-pz tracking-[0.3em] text-sm">
                WHAT'S INCLUDED
              </span>
            </div>
            <h2 className="bebas text-white text-3xl md:text-5xl leading-[0.95] mb-8">
              EVERY {service.name.toUpperCase()} <br />
              <span className="text-cyan-pz">DONE PROPERLY.</span>
            </h2>
            <ul className="space-y-3">
              {service.whatsIncluded.map((b) => (
                <li
                  key={b}
                  className="flex items-start gap-3 text-white/80 barlow leading-relaxed"
                >
                  <CheckCircle2 className="w-5 h-5 text-cyan-pz mt-1 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>

          <div data-testid="service-why-us">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-cyan-pz" />
              <span className="bebas text-cyan-pz tracking-[0.3em] text-sm">
                WHY POWERZ
              </span>
            </div>
            <h2 className="bebas text-white text-3xl md:text-5xl leading-[0.95] mb-8">
              SUNSHINE COAST'S <br />
              <span className="text-cyan-pz">SPECIALIST.</span>
            </h2>
            <ul className="space-y-3">
              {service.whyUs.map((b) => (
                <li
                  key={b}
                  className="flex items-start gap-3 text-white/80 barlow leading-relaxed"
                >
                  <Star className="w-5 h-5 text-cyan-pz mt-1 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* PRICING */}
      {service.pricing && (
        <section
          data-testid="service-pricing"
          className="relative bg-black py-20 md:py-28"
        >
          <div className="max-w-5xl mx-auto px-5 lg:px-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-cyan-pz" />
              <span className="bebas text-cyan-pz tracking-[0.3em] text-sm">
                PRICING
              </span>
            </div>
            <h2 className="bebas text-white text-3xl md:text-5xl leading-[0.95] mb-10">
              {service.pricing.label.toUpperCase()}
            </h2>
            <div className="border border-white/10 bg-[#0A0A0A] clip-perf divide-y divide-white/10">
              {service.pricing.items.map((it, i) => (
                <div
                  key={i}
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-6 py-5"
                >
                  <p className="text-white barlow text-lg">{it.name}</p>
                  <p className="bebas text-cyan-pz text-2xl tracking-wider">
                    {it.price}
                  </p>
                </div>
              ))}
            </div>
            {service.pricing.note && (
              <p className="mt-6 text-white/50 barlow text-sm leading-relaxed">
                {service.pricing.note}
              </p>
            )}
          </div>
        </section>
      )}

      {/* POLICY / IMPORTANT NOTES */}
      {service.policy && (
        <section
          data-testid="service-policy"
          className="relative bg-black py-12 md:py-16"
        >
          <div className="max-w-5xl mx-auto px-5 lg:px-8">
            <div className="border border-amber-400/40 bg-amber-400/5 p-6 md:p-8 clip-perf">
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle className="w-6 h-6 text-amber-400 shrink-0" />
                <h3 className="bebas text-amber-400 text-xl md:text-2xl tracking-[0.2em]">
                  {service.policy.title.toUpperCase()}
                </h3>
              </div>
              <ul className="space-y-3">
                {service.policy.bullets.map((b, i) => (
                  <li
                    key={i}
                    data-testid={`service-policy-bullet-${i}`}
                    className="flex items-start gap-3 text-white/85 barlow leading-relaxed"
                  >
                    <span className="bebas text-amber-400/80 tracking-widest text-sm mt-0.5 shrink-0">
                      0{i + 1}
                    </span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* PROCESS */}
      {service.process && service.process.length > 0 && (
        <section
          data-testid="service-process"
          className="relative bg-[#0A0A0A] py-20 md:py-28 border-y border-white/10"
        >
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-cyan-pz" />
              <span className="bebas text-cyan-pz tracking-[0.3em] text-sm">
                HOW IT WORKS
              </span>
            </div>
            <h2 className="bebas text-white text-3xl md:text-5xl leading-[0.95] mb-12">
              SIMPLE. <span className="text-cyan-pz">DONE PROPERLY.</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {service.process.map((p, i) => (
                <div
                  key={p.title}
                  className="relative border border-white/10 bg-black p-6 clip-perf"
                >
                  <span className="bebas text-cyan-pz/40 text-6xl absolute top-2 right-4 leading-none">
                    0{i + 1}
                  </span>
                  <h3 className="bebas text-white text-2xl tracking-wider mb-3 relative">
                    {p.title.toUpperCase()}
                  </h3>
                  <p className="text-white/70 barlow leading-relaxed relative">
                    {p.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* REVIEWS PULL-QUOTES */}
      {relatedReviews.length > 0 && (
        <section
          data-testid="service-reviews"
          className="relative bg-black py-20 md:py-28"
        >
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-cyan-pz" />
              <span className="bebas text-cyan-pz tracking-[0.3em] text-sm">
                REAL GOOGLE REVIEWS
              </span>
            </div>
            <h2 className="bebas text-white text-3xl md:text-5xl leading-[0.95] mb-12">
              WHAT SUNSHINE COAST LOCALS <br />
              <span className="text-cyan-pz">SAY ABOUT THIS JOB.</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedReviews.map((r, i) => (
                <figure
                  key={i}
                  data-testid={`service-review-${i}`}
                  className="border border-white/10 bg-[#0A0A0A] p-6 clip-perf"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {[0, 1, 2, 3, 4].map((s) => (
                      <Star
                        key={s}
                        className="w-4 h-4 fill-cyan-pz text-cyan-pz"
                      />
                    ))}
                  </div>
                  <blockquote className="text-white/80 barlow text-base leading-relaxed italic">
                    "{r.quote}"
                  </blockquote>
                  <figcaption className="mt-4 flex items-center justify-between">
                    <p className="bebas tracking-widest text-cyan-pz text-sm">
                      — {r.name}
                    </p>
                    <p className="text-white/40 text-xs barlow">{r.service}</p>
                  </figcaption>
                </figure>
              ))}
            </div>
            <div className="mt-10">
              <Link
                to="/reviews"
                data-testid="service-all-reviews-link"
                className="inline-flex items-center gap-3 border border-cyan-pz/40 text-cyan-pz px-6 py-3 bebas text-base tracking-[0.2em] hover:bg-cyan-pz hover:text-black transition-colors"
              >
                READ ALL 95+ GOOGLE REVIEWS
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {service.faq && service.faq.length > 0 && (
        <section
          data-testid="service-faq"
          className="relative bg-[#0A0A0A] py-20 md:py-28 border-y border-white/10"
        >
          <div className="max-w-4xl mx-auto px-5 lg:px-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-cyan-pz" />
              <span className="bebas text-cyan-pz tracking-[0.3em] text-sm">
                FAQ
              </span>
            </div>
            <h2 className="bebas text-white text-3xl md:text-5xl leading-[0.95] mb-10">
              {service.name.toUpperCase()} — <br />
              <span className="text-cyan-pz">QUESTIONS WE GET ASKED.</span>
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {service.faq.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="border-white/10"
                >
                  <AccordionTrigger
                    data-testid={`service-faq-q-${i}`}
                    className="bebas tracking-wider text-white text-lg md:text-xl py-5 hover:text-cyan-pz hover:no-underline"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent
                    data-testid={`service-faq-a-${i}`}
                    className="text-white/70 barlow text-base leading-relaxed whitespace-pre-line"
                  >
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      {/* RELATED SHOP */}
      {shopCategory && (
        <section className="relative bg-black py-16">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <Link
              to={`/shop?category=${shopCategory.slug}`}
              data-testid="service-shop-link"
              className="group flex flex-col md:flex-row items-start md:items-center justify-between gap-6 border border-cyan-pz/30 bg-[#0A0A0A] p-6 md:p-10 hover:border-cyan-pz hover:bg-cyan-pz/5 transition-colors clip-perf"
            >
              <div className="flex items-start gap-4">
                <ShoppingBag className="w-8 h-8 text-cyan-pz shrink-0 mt-1" />
                <div>
                  <p className="bebas text-cyan-pz tracking-[0.2em] text-xs mb-1">
                    BROWSE THE SHOP
                  </p>
                  <h3 className="bebas text-white text-2xl md:text-3xl tracking-wider">
                    {shopCategory.name.toUpperCase()}
                  </h3>
                  <p className="text-white/60 barlow text-sm mt-1">
                    {shopCategory.kicker}
                  </p>
                </div>
              </div>
              <ArrowRight className="w-6 h-6 text-cyan-pz group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </section>
      )}

      {/* RELATED BUILDS — internal links to /builds/<slug> for topical authority */}
      {relatedBuilds.length > 0 && (
        <section
          data-testid="service-related-builds"
          className="relative bg-black py-20 border-t border-white/10"
        >
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-cyan-pz" />
              <span className="bebas text-cyan-pz tracking-[0.3em] text-sm">
                COMPLETED JOBS
              </span>
            </div>
            <h2 className="bebas text-white text-3xl md:text-5xl leading-[0.9] mb-10">
              REAL {service.name.toUpperCase()} <br />
              <span className="text-cyan-pz">JOBS WE'VE FINISHED.</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedBuilds.map((b) => (
                <Link
                  key={b.slug}
                  to={`/builds/${b.slug}`}
                  data-testid={`service-related-build-${b.slug}`}
                  className="group block bg-[#0A0A0A] border border-white/10 hover:border-cyan-pz transition-colors overflow-hidden clip-perf"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={b.hero}
                      alt={b.title}
                      className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-5">
                    <p className="bebas text-cyan-pz/80 tracking-[0.2em] text-xs mb-1">
                      {b.suburb.toUpperCase()}
                    </p>
                    <h3 className="bebas text-white text-xl tracking-wider group-hover:text-cyan-pz transition-colors">
                      {b.title.toUpperCase()}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
            <div className="mt-8">
              <Link
                to="/builds"
                data-testid="service-all-builds-link"
                className="inline-flex items-center gap-3 border border-cyan-pz/40 text-cyan-pz px-6 py-3 bebas text-base tracking-[0.2em] hover:bg-cyan-pz hover:text-black transition-colors"
              >
                SEE ALL COMPLETED BUILDS
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* RELATED BLOG POSTS — internal-link block for topical SEO cluster */}
      {relatedPosts.length > 0 && (
        <section
          data-testid="service-related-blog"
          className="relative bg-[#0A0A0A] py-20 border-t border-white/10"
        >
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-cyan-pz" />
              <span className="bebas text-cyan-pz tracking-[0.3em] text-sm">
                FROM THE BLOG
              </span>
            </div>
            <h2 className="bebas text-white text-3xl md:text-5xl leading-[0.9] mb-10">
              MORE ON {service.name.toUpperCase()} <br />
              <span className="text-cyan-pz">& RELATED READING.</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedPosts.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  data-testid={`service-related-blog-${p.slug}`}
                  className="group block bg-black border border-white/10 hover:border-cyan-pz transition-colors overflow-hidden clip-perf"
                >
                  {p.image && (
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={p.image}
                        alt={p.title}
                        className="w-full h-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="p-5">
                    <p className="bebas text-cyan-pz/80 tracking-[0.2em] text-xs mb-2">
                      {(p.category || "GUIDE").toUpperCase()} • {p.readTime || ""}
                    </p>
                    <h3 className="bebas text-white text-xl tracking-wider group-hover:text-cyan-pz transition-colors leading-tight">
                      {p.title.toUpperCase()}
                    </h3>
                    {p.excerpt && (
                      <p className="text-white/60 barlow text-sm mt-3 line-clamp-3">
                        {p.excerpt}
                      </p>
                    )}
                    <span className="inline-flex items-center gap-2 mt-4 bebas text-cyan-pz tracking-[0.2em] text-sm">
                      READ MORE
                      <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
            <div className="mt-8">
              <Link
                to="/blog"
                data-testid="service-all-blog-link"
                className="inline-flex items-center gap-3 border border-cyan-pz/40 text-cyan-pz px-6 py-3 bebas text-base tracking-[0.2em] hover:bg-cyan-pz hover:text-black transition-colors"
              >
                SEE ALL BLOG POSTS
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* SERVICE AREA — suburb internal-link block for local SEO */}
      <section
        data-testid="service-suburbs"
        className="relative bg-[#0A0A0A] py-16 border-t border-white/10"
      >
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-10 bg-cyan-pz" />
            <span className="bebas text-cyan-pz tracking-[0.3em] text-sm">
              SERVICE AREA
            </span>
          </div>
          <h2 className="bebas text-white text-3xl md:text-5xl leading-[0.9] mb-3">
            {service.name.toUpperCase()} —<br />
            <span className="text-cyan-pz">SERVICING ALL SUNSHINE COAST.</span>
          </h2>
          <p className="text-white/60 barlow text-sm md:text-base max-w-3xl mb-8">
            Workshop at Kunda Park, customers from every Sunshine Coast suburb. Quick links to your local page:
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {featuredSuburbs.map((s) => (
              <Link
                key={s.slug}
                to={SUBURB_PATH(s.slug)}
                data-testid={`service-suburb-${s.slug}`}
                className="bg-black px-5 py-4 hover:bg-cyan-pz/10 transition-colors group inline-flex items-center gap-2"
              >
                <MapPin className="w-3.5 h-3.5 text-cyan-pz shrink-0" />
                <span className="bebas tracking-widest text-white group-hover:text-cyan-pz transition-colors text-sm">
                  AUTO ELECTRICIAN {s.name.toUpperCase()}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* OTHER SERVICES — internal-link block */}
      <section className="relative bg-[#0A0A0A] py-20 border-y border-white/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-10 bg-cyan-pz" />
            <span className="bebas text-cyan-pz tracking-[0.3em] text-sm">
              MORE SERVICES
            </span>
          </div>
          <h2 className="bebas text-white text-3xl md:text-5xl leading-[0.9] mb-10">
            WE ALSO DO.
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {otherServices.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`related-service-${s.slug}`}
                className="bg-black px-6 py-5 hover:bg-cyan-pz/10 transition-colors group"
              >
                <p className="bebas text-cyan-pz/60 tracking-widest text-xs">
                  {s.kicker.toUpperCase()}
                </p>
                <p className="bebas text-white tracking-wider text-xl group-hover:text-cyan-pz transition-colors mt-1">
                  {s.name.toUpperCase()}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Contact />
      <Footer />
    </div>
  );
}
